// filepath: c:\Users\2396375\assenivesh-ui\an-vue\src\components\dashboard\DigioKycStart.vue
<template>
  <section class="kyc-card">
    <h2>Complete KYC</h2>

    <p class="hint">
      You will complete the KYC steps in a secure Digio popup. After submission, verification will be reviewed.
    </p>

    <div class="actions">
      <button :disabled="loading || kycInProgress" @click="onStartClick" class="primary-btn">
        {{ loading ? "Starting..." : kycInProgress ? "KYC In Progress..." : "Start Video KYC" }}
      </button>

      <button v-if="digioInstance" :disabled="loading || kycInProgress" class="secondary-btn" @click="onCancelClick">
        Cancel
      </button>
    </div>

    <p v-if="statusMessage" class="status" :class="statusClass">{{ statusMessage }}</p>

    <details class="debug" v-if="debug && lastCallback">
      <summary>Debug: last Digio callback</summary>
      <pre>{{ lastCallback }}</pre>
    </details>
  </section>
</template>

<script>
import { useDigioPopup } from "@/composables/useDigioPopup";
import { useUserStore } from '@/store/user-store';

export default {
  name: "DigioKycStart",
  props: {
    user: {
      type: Object,
      required: true
    },
    debug: {
      type: Boolean,
      default: false
    }
  },
  emits: ['kyc-completed', 'kyc-failed', 'kyc-submitted'],
  data() {
    return {
      loading: false,
      kycInProgress: false, // NEW: Track entire KYC flow
      statusMessage: "",
      statusClass: "",
      digioInstance: null,
      lastCallback: null,
      requestId: null,
      customerIdentifier: null
    };
  },
  methods: {
    async onStartClick() {
      // Prevent duplicate KYC starts
      if (this.kycInProgress) {
        console.log('KYC already in progress');
        return;
      }

      this.loading = true;
      this.kycInProgress = true; // Lock the entire KYC flow
      this.statusMessage = "";
      this.statusClass = "";
      this.lastCallback = null;

      try {
        const store = useUserStore();

        // 1) Create request (server-side first)
        const response = await store.createKYCRequests({
          PAN: store.dashboardData?.pan || this.user.pan || this.user.panNumber,
          customer_identifier: store.dashboardData?.phoneNumber || this.user.phone || this.user.email,
          customer_name: `${store.dashboardData?.firstName || ''} ${store.dashboardData?.lastName || ''}`.trim() || this.user.name,
          reference_id: `AN-${this.user.id || Date.now()}`,
          transaction_id: `TXN-${Date.now()}`
        });

        if (!response.success) {
          throw new Error(response.message || "Failed to create KYC request");
        }

        const { requestId, tokenId, customerIdentifier } = response.data;
        this.requestId = requestId;
        this.customerIdentifier = customerIdentifier;

        // 2) Start Digio popup (Instantiation → init → submit)
        const { start } = useDigioPopup();
        this.digioInstance = start({
          requestId,
          identifier: customerIdentifier,
          tokenId,
          onCallback: (digioResponse) => {
            this.handleDigioCallback(digioResponse);
          }
        });

        // MVP message right after submit
        this.statusMessage = "KYC started. Please complete the steps in the Digio popup.";
        this.statusClass = "info";

      } catch (e) {
        this.statusMessage = e?.message || "Unable to start KYC.";
        this.statusClass = "error";
        this.kycInProgress = false; // Reset on error
        this.$emit('kyc-failed', e);
      } finally {
        this.loading = false;
        // Note: kycInProgress stays true until callback completes
      }
    },

    async handleDigioCallback(response) {
      this.lastCallback = JSON.stringify(response, null, 2);

      // Check for CSP violation error
      if (response && response.error_code === 'CSP_VIOLATION') {
        this.statusMessage = "⚠️ Network Security Policy Blocked KYC\n\n" +
          "Your organization's security policy (Zscaler/Firewall) is blocking the DigiLocker consent form.\n\n" +
          "Solutions:\n" +
          "1. Try from a personal network/mobile hotspot\n" +
          "2. Contact your IT team to whitelist: *.digilocker.gov.in and *.digio.in\n" +
          "3. Try from home network\n\n" +
          "Contact support for assistance.";
        this.statusClass = "error";
        this.kycInProgress = false; // Reset on error
        this.$emit('kyc-failed', response);
        return;
      }

      // Digio doc pattern: error_code indicates error/failure
      if (response && Object.prototype.hasOwnProperty.call(response, "error_code")) {
        this.statusMessage = "KYC flow failed or was cancelled. Please try again.";
        this.statusClass = "error";
        this.kycInProgress = false; // Reset on failure
        this.$emit('kyc-failed', response);
        return;
      }

      // Check KYC status after callback
      try {
        const store = useUserStore();
        const statusResponse = await store.getKYCRequestStatus({
          pan: store.dashboardData?.pan || this.user.pan || this.user.panNumber,
          customerIdentifier: this.customerIdentifier
        });

        if (statusResponse.success && statusResponse.data.Status === "Done") {
          this.statusMessage = "KYC completed successfully!";
          this.statusClass = "success";
          // Keep kycInProgress true - parent will handle navigation
          this.$emit('kyc-completed', statusResponse.data);
        } else {
          // Update KYC status to "Submitted" in backend
          await store.updateKYCRequestStatus({
            pan: store.dashboardData?.pan || this.user.pan || this.user.panNumber,
            customerIdentifier: this.customerIdentifier,
            status: "Submitted"
          });

          this.statusMessage = "KYC submitted successfully. Verification is under review and will be updated shortly.";
          this.statusClass = "info";
          // Keep kycInProgress true - parent will handle navigation

          // Emit submitted event to parent to move to pending state
          this.$emit('kyc-submitted', {
            pan: store.dashboardData?.pan || this.user.pan || this.user.panNumber,
            customerIdentifier: this.customerIdentifier,
            status: "Submitted"
          });
        }
      } catch (error) {
        console.error('Error checking/updating KYC status:', error);
        this.statusMessage = "KYC submitted. Status verification pending.";
        this.statusClass = "info";
        this.kycInProgress = false; // Reset on error
      }
    },

    onCancelClick() {
      try {
        const { cancel } = useDigioPopup();
        cancel(this.digioInstance);
        this.statusMessage = "KYC flow cancelled.";
        this.statusClass = "error";
        this.kycInProgress = false; // Reset on cancel
        this.$emit('kyc-failed', { cancelled: true });
      } catch (e) {
        this.statusMessage = e?.message || "Unable to cancel.";
        this.statusClass = "error";
        this.kycInProgress = false; // Reset on error
      }
    }
  }
};
</script>

<style scoped>
.kyc-card {
  padding: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
  max-width: 600px;
  margin: 0 auto;
}

h2 {
  color: #2c3e50;
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
}

.hint {
  color: #666;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.actions {
  margin-top: 20px;
  display: flex;
  gap: 12px;
}

.primary-btn {
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: #AB3498;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s;
}

.primary-btn:hover:not(:disabled) {
  background: #8e2a7d;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(171, 52, 152, 0.3);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #AB3498;
  transform: none;
}

.secondary-btn {
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  background: #fff;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s;
}

.secondary-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #bbb;
}

.secondary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.status.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status.info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.debug {
  margin-top: 16px;
  font-size: 13px;
}

.debug summary {
  cursor: pointer;
  color: #666;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
}

pre {
  background: #f7f7f7;
  padding: 12px;
  border-radius: 8px;
  overflow: auto;
  font-size: 12px;
  margin: 8px 0 0 0;
  border: 1px solid #e0e0e0;
}
</style>
